import { Controller } from "@hotwired/stimulus"

// Buyout index page — bid-package list: New/Edit package modal (create +
// update via fetch, row inserted/replaced in place — no reloads), styled
// delete-confirmation modal, row click-through to the package page, and the
// AI Package Builder modal (feature 70: chip PDF uploads → run → ~3s poll
// with a 4-minute client timeout so the spinner can never get stuck).
export default class extends Controller {
  static targets = [
    "tableWrap", "tbody", "emptyState",
    "packageModal", "packageModalTitle", "packageModalError", "packageSaveBtn",
    "pkgName", "pkgTrade", "pkgDue", "pkgEstimate", "pkgScope",
    "deleteModal", "deleteName",
    "importModal", "importCheckbox", "importAddCount", "importError", "importConfirmBtn",
    "aiModal", "aiFormState", "aiProgressState", "aiResultState", "aiResultBody",
    "aiDropzone", "aiFileInput", "aiChipList", "aiNotes", "aiError", "aiStartBtn"
  ]

  static values = {
    createUrl: String,
    packageUrlTemplate: String, // ".../buyout/0" — 0 swapped for the package id
    importUrl: String,
    aiPresignUrl: String,
    aiCreateUrl: String,
    aiStatusUrlTemplate: String // ".../ai_runs/0" — 0 swapped for the run id
  }

  connect() {
    this._editingId = null
    this._pendingDeleteId = null
    this._aiFiles = []       // uploaded-to-tmp PDFs waiting for the run
    this._aiPolling = false  // a run is in flight (poll keeps going even if the modal is hidden)
    this._aiPollTimer = null
  }

  disconnect() {
    if (this._aiPollTimer) clearTimeout(this._aiPollTimer)
  }

  // ── Row navigation ──────────────────────────────────────────────────

  openPackageRow(event) {
    if (event.target.closest("button") || event.target.closest("a")) return
    const url = event.currentTarget.dataset.url
    if (url) window.location.href = url
  }

  // ── New/Edit modal ──────────────────────────────────────────────────

  showNewModal(event) {
    event?.preventDefault()
    this._editingId = null
    this.packageModalTitleTarget.textContent = "New Package"
    this.pkgNameTarget.value = ""
    this.pkgTradeTarget.value = ""
    this._setDate("")
    this.pkgEstimateTarget.value = ""
    this.pkgScopeTarget.value = ""
    this._clearModalError()
    this.packageModalTarget.classList.remove("hidden")
    this.pkgNameTarget.focus()
  }

  showEditModal(event) {
    event.preventDefault()
    event.stopPropagation()
    const row = event.currentTarget.closest("tr")
    this._editingId = row.dataset.packageId
    this.packageModalTitleTarget.textContent = "Edit Package"
    this.pkgNameTarget.value = row.dataset.packageName || ""
    this.pkgTradeTarget.value = row.dataset.packageTrade || ""
    this._setDate(row.dataset.packageDue || "")
    this.pkgEstimateTarget.value = row.dataset.packageEstimate || ""
    this.pkgScopeTarget.value = row.dataset.packageScope || ""
    this._clearModalError()
    this.packageModalTarget.classList.remove("hidden")
    this.pkgNameTarget.focus()
  }

  hidePackageModal(event) {
    event?.preventDefault()
    this.packageModalTarget.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  async savePackage(event) {
    event.preventDefault()
    const name = this.pkgNameTarget.value.trim()
    if (!name) {
      this._modalError("Give the package a name.")
      return
    }

    const payload = {
      bid_package: {
        name,
        trade: this.pkgTradeTarget.value.trim(),
        bids_due_at: this.pkgDueTarget.value.trim(),
        estimate_amount: this.pkgEstimateTarget.value.trim(),
        scope_sheet: this.pkgScopeTarget.value
      }
    }

    const isEdit = !!this._editingId
    const url = isEdit ? this._packageUrl(this._editingId) : this.createUrlValue
    this.packageSaveBtnTarget.disabled = true

    try {
      const res = await fetch(url, {
        method: isEdit ? "PATCH" : "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify(payload)
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Something went wrong")

      if (isEdit) {
        const row = document.getElementById(`bid-package-row-${this._editingId}`)
        if (row) row.outerHTML = data.row
        this._showToast("Package updated")
      } else {
        this.tbodyTarget.insertAdjacentHTML("afterbegin", data.row)
        this.tableWrapTarget.classList.remove("hidden")
        this.emptyStateTarget.classList.add("hidden")
        this._showToast("Package created")
      }
      this.hidePackageModal()
    } catch (e) {
      this._modalError(e.message)
    } finally {
      this.packageSaveBtnTarget.disabled = false
    }
  }

  // ── Delete (styled confirm modal — never browser confirm()) ─────────

  showDeleteModal(event) {
    event.preventDefault()
    event.stopPropagation()
    const row = event.currentTarget.closest("tr")
    this._pendingDeleteId = row.dataset.packageId
    this.deleteNameTarget.textContent = row.dataset.packageName || "This package"
    this.deleteModalTarget.classList.remove("hidden")
  }

  hideDeleteModal(event) {
    event?.preventDefault()
    this.deleteModalTarget.classList.add("hidden")
    this._pendingDeleteId = null
  }

  async confirmDelete(event) {
    event.preventDefault()
    const id = this._pendingDeleteId
    if (!id) return

    try {
      const res = await fetch(this._packageUrl(id), {
        method: "DELETE",
        headers: this._jsonHeaders()
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Failed to delete package")

      document.getElementById(`bid-package-row-${id}`)?.remove()
      if (!this.tbodyTarget.querySelector("tr")) {
        this.tableWrapTarget.classList.add("hidden")
        this.emptyStateTarget.classList.remove("hidden")
      }
      this._showToast("Package deleted")
    } catch (e) {
      this._showToast(e.message, "error")
    } finally {
      this.hideDeleteModal()
    }
  }

  // ── Import From Winning Bid (feature 69) ────────────────────────────

  showImportModal(event) {
    event?.preventDefault()
    if (!this.hasImportModalTarget) return
    this._clearImportError()
    this.importModalTarget.classList.remove("hidden")
  }

  hideImportModal(event) {
    event?.preventDefault()
    this.importModalTarget.classList.add("hidden")
  }

  importSelectionChanged() {
    if (!this.hasImportAddCountTarget) return
    this.importAddCountTarget.textContent = this.importCheckboxTargets.filter(cb => cb.checked).length
  }

  async confirmImport(event) {
    event.preventDefault()
    const categoryIds = this.importCheckboxTargets.filter(cb => cb.checked).map(cb => cb.dataset.categoryId)
    if (categoryIds.length === 0) {
      this._importError("Pick at least one category to import.")
      return
    }

    this.importConfirmBtnTarget.disabled = true
    this.importConfirmBtnTarget.textContent = "Importing..."
    try {
      const res = await fetch(this.importUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({ category_ids: categoryIds })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Failed to import your winning bid")

      ;(data.rows || []).forEach(row => {
        this.tbodyTarget.insertAdjacentHTML("afterbegin", row)
      })
      this.tableWrapTarget.classList.remove("hidden")
      this.emptyStateTarget.classList.add("hidden")
      this.hideImportModal()
      const n = data.packages_count || 0
      this._showToast(`${n} draft ${n === 1 ? "package" : "packages"} created from your winning bid`)
    } catch (e) {
      this._importError(e.message)
    } finally {
      this.importConfirmBtnTarget.disabled = false
      this.importConfirmBtnTarget.textContent = "Create Draft Packages"
    }
  }

  // ── AI Package Builder (feature 70) ─────────────────────────────────

  showAiModal(event) {
    event?.preventDefault()
    if (!this._aiPolling) this._resetAiModal()
    this.aiModalTarget.classList.remove("hidden")
  }

  // Hiding never cancels the run — polling continues and the result lands
  // in the table (plus a toast) whenever it finishes.
  hideAiModal(event) {
    event?.preventDefault()
    this.aiModalTarget.classList.add("hidden")
  }

  triggerAiFileInput(event) {
    event.preventDefault()
    this.aiFileInputTarget.click()
  }

  handleAiFiles(event) {
    this._addAiFiles(Array.from(event.target.files || []))
    event.target.value = ""
  }

  aiDragOver(event) {
    event.preventDefault()
    this.aiDropzoneTarget.classList.add("border-brand", "bg-blue-50")
  }

  aiDragLeave(event) {
    event.preventDefault()
    this.aiDropzoneTarget.classList.remove("border-brand", "bg-blue-50")
  }

  aiDrop(event) {
    event.preventDefault()
    this.aiDropzoneTarget.classList.remove("border-brand", "bg-blue-50")
    this._addAiFiles(Array.from(event.dataTransfer?.files || []))
  }

  _addAiFiles(files) {
    const pdfs = files.filter(f => /\.pdf$/i.test(f.name))
    if (pdfs.length < files.length) this._showToast("Only PDFs can be read by the AI — other files were skipped", "error")
    pdfs.forEach(file => this._aiPresignAndPut(file))
  }

  async _aiPresignAndPut(file) {
    const chip = this._buildAiChip(file.name, true)
    this.aiChipListTarget.appendChild(chip)

    try {
      const presignRes = await fetch(this.aiPresignUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({
          files: [{ filename: file.name, content_type: file.type || "application/pdf", byte_size: file.size }]
        })
      })
      if (!presignRes.ok) {
        const err = await presignRes.json().catch(() => ({}))
        throw new Error(err.error || "Couldn't start the upload")
      }
      const { uploads } = await presignRes.json()
      const upload = uploads[0]

      const putRes = await fetch(upload.upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type || upload.content_type },
        body: file
      })
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status}) — please retry`)

      this._aiFiles.push({ cache_name: upload.cache_name, filename: upload.filename })
      this._finishAiChip(chip, upload.cache_name, upload.filename)
    } catch (e) {
      chip.remove()
      this._aiError(e.message)
    }
  }

  removeAiFile(event) {
    event.preventDefault()
    const chip = event.currentTarget.closest("[data-cache-name]")
    if (!chip) return
    this._aiFiles = this._aiFiles.filter(f => f.cache_name !== chip.dataset.cacheName)
    chip.remove()
  }

  async startAiRun(event) {
    event.preventDefault()
    if (this._aiFiles.length === 0) {
      this._aiError("Upload your drawing or spec set (PDF) first.")
      return
    }

    this.aiStartBtnTarget.disabled = true
    this._clearAiError()
    try {
      const res = await fetch(this.aiCreateUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({ files: this._aiFiles, notes: this.aiNotesTarget.value.trim() })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Couldn't start the AI run — please try again")

      this._aiPolling = true
      this._aiShowState("progress")
      this._pollAiRun(data.run_id, Date.now())
    } catch (e) {
      this._aiError(e.message)
    } finally {
      this.aiStartBtnTarget.disabled = false
    }
  }

  // Poll every ~3s; give up client-side after 4 minutes (the run keeps
  // going server-side — the message says to refresh later). A few network
  // hiccups in a row also stop the spinner instead of hanging forever.
  async _pollAiRun(runId, startedAt, misses = 0) {
    if (Date.now() - startedAt > 240000) {
      this._aiFinish()
      this._aiFail("This is taking longer than expected — the run is still working in the background. Refresh this page in a few minutes to see the new packages.")
      return
    }

    let data = null
    try {
      const url = this.aiStatusUrlTemplateValue.replace(/\/0(\/|$)/, `/${runId}$1`)
      const res = await fetch(url, { headers: this._jsonHeaders() })
      data = await res.json()
      if (!res.ok) throw new Error(data.error || "status check failed")
    } catch (e) {
      if (misses >= 4) {
        this._aiFinish()
        this._aiFail("Lost contact while checking on the run — refresh this page in a minute to see the results.")
        return
      }
      this._aiPollTimer = setTimeout(() => this._pollAiRun(runId, startedAt, misses + 1), 3000)
      return
    }

    if (data.status === "done") {
      this._aiFinish()
      this.aiResultBodyTarget.innerHTML = data.summary || ""
      this._aiShowState("result")
      ;(data.rows || []).forEach(row => this.tbodyTarget.insertAdjacentHTML("afterbegin", row))
      if ((data.rows || []).length > 0) {
        this.tableWrapTarget.classList.remove("hidden")
        this.emptyStateTarget.classList.add("hidden")
      }
      const n = (data.rows || []).length
      this._showToast(`AI Package Builder finished — ${n} draft ${n === 1 ? "package" : "packages"} created`)
    } else if (data.status === "failed") {
      this._aiFinish()
      this._aiFail(data.error || "AI couldn't read these drawings — you can still create packages manually.")
    } else {
      this._aiPollTimer = setTimeout(() => this._pollAiRun(runId, startedAt, 0), 3000)
    }
  }

  _aiFinish() {
    this._aiPolling = false
    if (this._aiPollTimer) clearTimeout(this._aiPollTimer)
    this._aiPollTimer = null
  }

  // Failure path: back to the form (files kept so they can retry) with the
  // error shown — and a toast in case the modal was closed meanwhile.
  _aiFail(message) {
    this._aiShowState("form")
    this._aiError(message)
    if (this.aiModalTarget.classList.contains("hidden")) this._showToast(message, "error")
  }

  _aiShowState(state) {
    this.aiFormStateTarget.classList.toggle("hidden", state !== "form")
    this.aiProgressStateTarget.classList.toggle("hidden", state !== "progress")
    this.aiResultStateTarget.classList.toggle("hidden", state !== "result")
  }

  _resetAiModal() {
    this._aiFiles = []
    this.aiChipListTarget.innerHTML = ""
    this.aiNotesTarget.value = ""
    this._clearAiError()
    this._aiShowState("form")
  }

  _buildAiChip(name, uploading) {
    const chip = document.createElement("span")
    chip.className = uploading
      ? "inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-[13px] font-medium"
      : "inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-[13px] font-medium"
    if (uploading) {
      chip.innerHTML = '<svg class="w-3.5 h-3.5 animate-spin shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>'
    }
    const label = document.createElement("span")
    label.className = "truncate max-w-[220px]"
    label.textContent = name
    chip.appendChild(label)
    return chip
  }

  _finishAiChip(chip, cacheName, filename) {
    chip.className = "inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-[13px] font-medium"
    chip.dataset.cacheName = cacheName
    chip.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5 shrink-0"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg>'
    const label = document.createElement("span")
    label.className = "truncate max-w-[220px]"
    label.textContent = filename
    chip.appendChild(label)
    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "inline-flex items-center justify-center w-4 h-4 rounded-full text-gray-400 hover:text-red-600 cursor-pointer shrink-0"
    removeBtn.setAttribute("data-action", "click->buyout#removeAiFile")
    removeBtn.setAttribute("title", "Remove file")
    removeBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-3.5 h-3.5 pointer-events-none"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>'
    chip.appendChild(removeBtn)
  }

  _aiError(message) {
    this.aiErrorTarget.textContent = message
    this.aiErrorTarget.classList.remove("hidden")
  }

  _clearAiError() {
    this.aiErrorTarget.textContent = ""
    this.aiErrorTarget.classList.add("hidden")
  }

  _importError(message) {
    this.importErrorTarget.textContent = message
    this.importErrorTarget.classList.remove("hidden")
  }

  _clearImportError() {
    if (!this.hasImportErrorTarget) return
    this.importErrorTarget.textContent = ""
    this.importErrorTarget.classList.add("hidden")
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  _packageUrl(id) {
    return this.packageUrlTemplateValue.replace(/\/0$/, `/${id}`)
  }

  _setDate(value) {
    const input = this.pkgDueTarget
    input.value = value || ""
    if (input._flatpickr) input._flatpickr.setDate(value || null, false)
  }

  _modalError(message) {
    this.packageModalErrorTarget.textContent = message
    this.packageModalErrorTarget.classList.remove("hidden")
  }

  _clearModalError() {
    this.packageModalErrorTarget.textContent = ""
    this.packageModalErrorTarget.classList.add("hidden")
  }

  _jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
